import { format } from 'date-fns'

import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import { buildInstrumentIndex, resolveUnderlyingInstrument } from '@/lib/instruments/underlying-ref'
import { safeParseFloat } from '@/lib/utils'
import { UTC } from '@/lib/utils/utc-day'
import { AssetClass, Exchange, Instrument, InstrumentKind } from '@/types/generated/common/types'

// Product-defined majors always lead the picker in this exact order,
// regardless of venue availability or volume.
const MAJOR_ASSETS = ['BTC', 'ETH', 'SOL', 'HYPE', 'XRP', 'TRX']

// Human-readable labels for the enrichment pipeline's asset-class taxonomy.
// ASSET_CLASS_UNSPECIFIED is deliberately excluded — that's "not yet
// classified", not a real filterable value.
const ASSET_CLASS_LABELS: Partial<Record<AssetClass, string>> = {
  [AssetClass.ASSET_CLASS_CRYPTO]: 'Crypto',
  [AssetClass.ASSET_CLASS_EQUITY]: 'Equity',
  [AssetClass.ASSET_CLASS_COMMODITY]: 'Commodity',
  [AssetClass.ASSET_CLASS_FX]: 'FX',
  [AssetClass.ASSET_CLASS_INDEX]: 'Index',
  [AssetClass.ASSET_CLASS_RATE]: 'Rate',
}

// Fixed display order for the labels above.
const ASSET_CLASS_ORDER: AssetClass[] = [
  AssetClass.ASSET_CLASS_CRYPTO,
  AssetClass.ASSET_CLASS_EQUITY,
  AssetClass.ASSET_CLASS_COMMODITY,
  AssetClass.ASSET_CLASS_FX,
  AssetClass.ASSET_CLASS_INDEX,
  AssetClass.ASSET_CLASS_RATE,
]

export const assetClassLabel = (assetClass: AssetClass): string =>
  ASSET_CLASS_LABELS[assetClass] ?? 'Other'

// Which asset classes actually appear across the given instruments, in a
// fixed display order. Derived from the live instrument set rather than a
// hardcoded list — enrichment coverage (and which classes exist at all)
// varies as new venues and instrument types land.
export function collectAssetClasses(instruments: Instrument[]): AssetClass[] {
  const present = new Set<AssetClass>()
  for (const instrument of instruments) {
    const assetClass = instrument.enrichment?.assetClass
    if (assetClass !== undefined && assetClass !== AssetClass.ASSET_CLASS_UNSPECIFIED) {
      present.add(assetClass)
    }
  }
  return ASSET_CLASS_ORDER.filter((assetClass) => present.has(assetClass))
}

export interface WizardAsset {
  /** Unique group identity — used for React/Virtuoso reconciliation, section
   *  decoration lookup, "did the selected asset change" comparisons, and
   *  wizardGroupKey. */
  id: string
  /** Display/search/sort key — the bare ticker (e.g. "BTC", "IBIT"), never prefixed.
   *  What the UI shows and what typed queries match against. */
  key: string
  label: string
  instruments: Instrument[]
  /** Precomputed once in `groupByAsset` so `sortAssets`/`compareAssetsForSearch`
   *  don't re-scan every instrument in the group (options chains can run into
   *  the thousands) on every single comparator call during `.sort()`. */
  volume24h: number
  hasDeribit: boolean
  /** The class this asset trades as (first classified instrument found),
   *  `undefined` when nothing in the group is classified yet. Precomputed for
   *  the same reason as `volume24h`/`hasDeribit` — this used to be rescanned
   *  by every caller (the wizard's row label, section building) on every
   *  render, including ones that had nothing to do with this asset's data
   *  actually changing. */
  assetClass: AssetClass | undefined
}

const assetDisplayKey = (instrument: Instrument): string =>
  instrument.enrichment?.canonicalGroup || instrument.baseCurrency

// The asset group of ONE instrument taken alone — venue-agnostic, so CME's BTC futures
// (GLBX) and an OPRA chain tagged "BTC" land in the same row as the crypto venues' BTC,
// under its Futures/Options sections. Safe for representative picking: the asset row's
// representative is ranked perp-first (a crypto perp wins over a CME future), and the
// venue pane picks per exchange, so a GLBX/OPRA row still resolves to its own contract.
//
// It cannot see a cross-exchange underlying pointer, because a pointer only means
// anything relative to the pool the underlying might be in — use `resolveAssetGroup`
// when a pool is in hand.
export const assetGroupId = (instrument: Instrument): string => assetDisplayKey(instrument)

// US-listed spot (and BITO's futures-based) crypto ETFs/trusts → the crypto they track. Nothing
// in the instrument metadata makes that link: the XNAS ETF, its OPRA options and Bybit's
// tokenized perp all come back tagged base/canonicalGroup "IBIT", never "BTC". Kept here, in
// one place, until enrichment carries it. Fund tickers only — a crypto token that shares one
// (HODL the token vs VanEck's HODL) is not a fund, so crypto-class instruments never map.
const TRACKED_CRYPTO: Record<string, string> = {
  IBIT: 'BTC',
  FBTC: 'BTC',
  GBTC: 'BTC',
  BITB: 'BTC',
  ARKB: 'BTC',
  BTCO: 'BTC',
  HODL: 'BTC',
  BRRR: 'BTC',
  EZBC: 'BTC',
  BTCW: 'BTC',
  BITO: 'BTC',
  ETHA: 'ETH',
  FETH: 'ETH',
  ETHE: 'ETH',
  ETHW: 'ETH',
  CETH: 'ETH',
  EZET: 'ETH',
  QETH: 'ETH',
  ETHV: 'ETH',
}

/**
 * The asset a group stands for: BTC for BTC itself and for IBIT/FBTC/…; else itself. Pass the
 * instrument's asset class when known — a crypto-class group is always itself.
 */
export const trackedAssetOf = (group: string, assetClass?: AssetClass): string => {
  const upper = group.toUpperCase()
  if (assetClass === AssetClass.ASSET_CLASS_CRYPTO) return upper
  return TRACKED_CRYPTO[upper] ?? upper
}

/**
 * Whether `instrument` is a market on `base` (upper-case): its own base currency, its asset
 * group (so CME's BTC futures/options, tagged canonicalGroup "BTC", count), or an ETF tracking
 * it (IBIT, its options and perps). A pair merely quoted in it (BTC-BLAST) is not.
 */
export const matchesBaseAsset = (instrument: Instrument, base: string): boolean =>
  instrument.baseCurrency.toUpperCase() === base ||
  trackedAssetOf(assetGroupId(instrument), instrument.enrichment?.assetClass) === base

/**
 * The asset group an instrument belongs to, resolved against a pool of instruments.
 *
 * An instrument carrying a cross-exchange `underlying` pointer joins its underlying's
 * group rather than forming one of its own: an OPRA IBIT option lands in the same row
 * as the XNAS IBIT equity it delivers.
 *
 * On today's catalog this changes nothing, and that is worth stating plainly rather
 * than discovering later. Both rows come back tagged `baseCurrency: "IBIT"` and
 * `canonicalGroup: "IBIT"`, so `assetGroupId` already returns `IBIT` for each and they
 * merge with no pointer involved. What this function adds is independence from that
 * coincidence: grouping by the pointer holds when the two rows disagree on their tags —
 * an option tagged from the asset it tracks rather than from its OSI root — where the
 * tag-derived path would split them.
 *
 * The group takes the UNDERLYING's identity, not the derivative's, so the row reads as
 * "IBIT" with its option chain inside — never as an options group that happens to
 * contain an equity. Today both rows resolve to the same identity either way, so this
 * too is currently indistinguishable from doing nothing; it decides the tie the moment
 * the two stop agreeing.
 *
 * An unresolvable pointer (the underlying is absent from this pool, or delisted) falls
 * through to the instrument's own group. The pointer carries no liveness guarantee, so
 * that is an ordinary outcome, not an error.
 */
export const resolveAssetGroup = (
  instrument: Instrument,
  index: Map<string, Instrument>
): { id: string; key: string } => {
  const owner = resolveUnderlyingInstrument(instrument, index) ?? instrument
  return { id: assetGroupId(owner), key: assetDisplayKey(owner) }
}

const titleCase = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

// `enrichment.displayName` names a *contract*, not an asset: the BTC group
// holds "Bitcoin" (the perp) alongside "Bitcoin 14 Aug 26 Future" and the SOL
// group holds "Solana" alongside "Solana / ETH". Taking whichever one happens
// to come first in the catalog therefore labels the asset arbitrarily, which
// is how "BTC — Bitcoin 14 Aug 26 Future · Crypto" ends up on screen.
//
// A perp or spot pair names its underlying plainly; dated futures and options
// carry expiry/strike, so they only get a say when nothing better exists.
const displayNameRank = (instrument: Instrument): number => {
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE) {
    return isPerpInstrument(instrument) ? 0 : 2
  }
  if (instrument.kind === InstrumentKind.INSTRUMENT_KIND_SPOT) return 1
  return 3
}

// Enrichment `displayName` still names a *contract type*, not just the asset
// — a perp's displayName is typically "Bitcoin Perpetual", not "Bitcoin", and
// an option's is a full contract name like "Bitcoin 11 Sep 26 60000 Put". The
// wizard's asset-level label is meant to identify the underlying only
// (contract type is already conveyed by the Perp/Spot/Futures/Options section
// it sits in), so both suffixes are stripped here rather than left for every
// caller to strip individually.
export const stripContractSuffix = (name: string): string =>
  name
    .replace(/\s+Perpetual$/i, '')
    .replace(/\s+\d{1,2}\s+[A-Za-z]{3}\s+\d{2}\s+[\d.]+\s+(Call|Put)\s*$/i, '')
    .trim()

// Prefer the real enrichment display name (e.g. "Bitcoin") over a naive
// title-case of the asset key (e.g. "BTC" -> "Btc") — mirrors the iconTag
// group-fallback pattern used for the group icon: scan the group's
// instruments for one with enrichment data before falling back.
export const assetGroupLabel = (key: string, instruments: Instrument[]): string => {
  let best: string | undefined
  let bestRank = Number.POSITIVE_INFINITY
  // Whether `best` came from an option contract — only possible when the
  // asset trades EXCLUSIVELY as options (a perp/spot/future would always
  // outrank it), in which case the stripped name reads as just the
  // underlying ("Bitcoin") with nothing marking it as an options market —
  // append "Options" so it doesn't look identical to a perp/spot asset row.
  let bestIsOption = false

  for (const instrument of instruments) {
    const displayName = instrument.enrichment?.displayName
    if (!displayName) continue
    const rank = displayNameRank(instrument)
    if (rank > bestRank) continue
    const stripped = stripContractSuffix(displayName)
    // Within a rank, the shortest name is the least contract-specific one —
    // "Solana" over "Solana / ETH" among a venue's spot pairs.
    if (rank === bestRank && best !== undefined && stripped.length >= best.length) continue
    best = stripped
    bestRank = rank
    bestIsOption = instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
  }

  if (best === undefined) return titleCase(key)
  return bestIsOption ? `${best} Options` : best
}

export function groupByAsset(instruments: Instrument[]): WizardAsset[] {
  const active = instruments.filter((instrument) => instrument.isActive)
  // Indexed over the ACTIVE set, so a pointer at a delisted underlying resolves the
  // same way as one at an underlying this pool never held — the derivative keeps its
  // own group rather than founding a group named after a row nobody can see.
  const index = buildInstrumentIndex(active)

  const groups = new Map<string, { key: string; instruments: Instrument[] }>()
  for (const instrument of active) {
    const { id, key } = resolveAssetGroup(instrument, index)
    const existing = groups.get(id)
    if (existing) {
      existing.instruments.push(instrument)
    } else {
      // `key` is the resolved OWNER's display key, so a group founded by an option
      // that arrives before its underlying is still named after the underlying.
      groups.set(id, { key, instruments: [instrument] })
    }
  }
  return [...groups.entries()].map(([id, { key, instruments: groupInstruments }]) => ({
    id,
    key,
    label: assetGroupLabel(key, groupInstruments),
    instruments: groupInstruments,
    volume24h: assetVolume(groupInstruments),
    hasDeribit: isAvailableOnDeribit(groupInstruments),
    assetClass: representativeAssetClass(groupInstruments),
  }))
}

// A quote currency counts as an option's "default" settlement — not worth
// spelling out in a label — when it's the conventional USD quote, or when it
// equals the base currency itself (an inverse option, e.g. BTC options
// settled in BTC rather than USD/USDC). Shared with the venue-pane grouping
// (watchlist-wizard-grouping.ts) so both panels agree on which quotes get
// called out.
const DEFAULT_OPTION_QUOTE_CURRENCY = 'USD'

export const isDefaultOptionQuote = (baseCurrency: string, quoteCurrency: string): boolean =>
  !quoteCurrency ||
  quoteCurrency.toUpperCase() === DEFAULT_OPTION_QUOTE_CURRENCY ||
  quoteCurrency.toUpperCase() === baseCurrency.toUpperCase()

// Strips every separator so a query typed without them still matches a venue
// that uses them: "btcusdt" against "BTC-USDT", "BTC_USDT" and "BTC/USDT"
// alike. Users don't know (and shouldn't have to know) which separator a
// given venue picked for the same pair.
const squash = (value: string): string => value.replace(/[^a-z0-9]/g, '')

// Whitespace splits a query into tokens that must *all* match, though not
// necessarily the same field — "solana krw" matches an instrument whose
// display name carries "Solana" and whose symbol carries "KRW", which a
// single-string `includes` over any one field could never do.
export const queryTokens = (query: string): string[] =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean)

const tokenMatchesValue = (value: string | undefined, token: string): boolean => {
  if (!value) return false
  const lower = value.toLowerCase()
  if (lower.includes(token)) return true
  const squashedToken = squash(token)
  return squashedToken.length > 0 && squash(lower).includes(squashedToken)
}

const matchesAllTokens = (values: (string | undefined)[], tokens: string[]): boolean =>
  tokens.every((token) => values.some((value) => tokenMatchesValue(value, token)))

const instrumentFields = (instrument: Instrument): (string | undefined)[] => [
  instrument.symbol,
  instrument.baseCurrency,
  instrument.enrichment?.displayName,
]

// Matches the search query against everything a user might type: the raw
// symbol, the venue base currency, or the human-readable enrichment name
// (e.g. "Bitcoin" as well as "BTC").
export function instrumentMatchesQuery(instrument: Instrument, query: string): boolean {
  const tokens = queryTokens(query)
  if (tokens.length === 0) return true
  return matchesAllTokens(instrumentFields(instrument), tokens)
}

// A group-level match (the asset key/label itself matching the query, e.g.
// searching "Bitcoin" or "BTC") keeps every instrument in that asset —
// otherwise the group survives only with its matching instruments.
export function assetMatchesQuery(asset: WizardAsset, query: string): boolean {
  const tokens = queryTokens(query)
  if (tokens.length === 0) return true
  return matchesAllTokens([asset.key, asset.label], tokens)
}

// Did the user type this asset's identity outright? Trailing "!" is stripped
// because that's continuous-futures notation (BTC1!), so typing the
// continuous symbol still resolves to the underlying.
export function isExactAssetMatch(asset: WizardAsset, query: string): boolean {
  const normalized = query.trim().toUpperCase().replace(/!$/, '')
  if (!normalized) return false
  if (asset.key.toUpperCase() === normalized) return true
  return asset.instruments.some(
    (instrument) =>
      instrument.symbol.toUpperCase() === normalized ||
      instrument.baseCurrency.toUpperCase() === normalized
  )
}

const fieldMatchIndex = (value: string | undefined, query: string): number => {
  if (!value) return Number.POSITIVE_INFINITY
  const index = value.toLowerCase().indexOf(query)
  return index === -1 ? Number.POSITIVE_INFINITY : index
}

const bestFieldMatchIndex = (values: (string | undefined)[], query: string): number =>
  values.reduce(
    (best, value) => Math.min(best, fieldMatchIndex(value, query)),
    Number.POSITIVE_INFINITY
  )

// How early the query matched within an asset — 0 means it matched right at
// the start of the group name or an instrument field (e.g. "et" against the
// very start of "Ethereum"), higher means it matched further in (e.g. "et"
// inside "...PERPETUAL"). Sorting search results by this ascending surfaces
// the identity match ("Ethereum") ahead of the incidental one ("Bitcoin
// Perpetual") instead of falling back to the unrelated major-asset/volume
// order everything else uses.
export function assetSearchRank(asset: WizardAsset, query: string): number {
  // Earliness only makes sense against a single needle — a multi-token query
  // is ranked on its first token and separated by the tiebreaks below.
  const normalized = queryTokens(query)[0] ?? ''
  if (!normalized) return 0
  const groupRank = bestFieldMatchIndex([asset.key, asset.label], normalized)
  if (groupRank !== Number.POSITIVE_INFINITY) return groupRank
  return asset.instruments.reduce(
    (best, instrument) =>
      Math.min(
        best,
        bestFieldMatchIndex(
          [instrument.symbol, instrument.baseCurrency, instrument.enrichment?.displayName],
          normalized
        )
      ),
    Number.POSITIVE_INFINITY
  )
}

// Search ordering, deliberately *not* the browse ordering. Once the user has
// typed something they have already said what they want, so the curated
// `MAJOR_ASSETS` list and the Deribit-availability tiebreak that `sortAssets`
// applies have no business outranking that. Three objective keys instead:
//
//   1. exact identity match — you typed the ticker, you get the ticker
//   2. how early the query matched — "et" is Ethereum, not BTC-PERPETUAL
//   3. 24h volume — the biggest market wins an otherwise even match
export function compareAssetsForSearch(a: WizardAsset, b: WizardAsset, query: string): number {
  const exactDiff = Number(isExactAssetMatch(b, query)) - Number(isExactAssetMatch(a, query))
  if (exactDiff !== 0) return exactDiff

  const rankDiff = assetSearchRank(a, query) - assetSearchRank(b, query)
  if (rankDiff !== 0) return rankDiff

  return b.volume24h - a.volume24h
}

export function filterAssetsByQuery(assets: WizardAsset[], query: string): WizardAsset[] {
  if (!query.trim()) return assets
  const matched = assets.flatMap((asset) => {
    if (assetMatchesQuery(asset, query)) return [asset]
    const matchingInstruments = asset.instruments.filter((instrument) =>
      instrumentMatchesQuery(instrument, query)
    )
    return matchingInstruments.length > 0 ? [{ ...asset, instruments: matchingInstruments }] : []
  })
  // Ties keep the incoming order — `sort` is stable.
  return matched.sort((a, b) => compareAssetsForSearch(a, b, query))
}

export interface CappedWizardAsset extends WizardAsset {
  hiddenMatchCount?: number
}

// A single group-name match (e.g. "btc") keeps every instrument in that asset
// (see `filterAssetsByQuery` above) — for majors with deep options chains
// that's several thousand leaf rows. The wizard's virtual list only
// virtualizes at the asset-group level, so an unbounded group renders its
// entire subtree as real DOM the moment its Virtuoso item mounts (measured:
// ~4k rows for a broad "btc" query, several hundred ms of blocked main
// thread). Cap each group's rendered instrument list; callers that need the
// true match count should keep using the pre-cap `assets` list.
export const SEARCH_LEAF_CAP = 150

export function capAssetsForDisplay(
  assets: WizardAsset[],
  cap: number = SEARCH_LEAF_CAP
): CappedWizardAsset[] {
  return assets.map((asset) =>
    asset.instruments.length > cap
      ? {
          ...asset,
          instruments: asset.instruments.slice(0, cap),
          hiddenMatchCount: asset.instruments.length - cap,
        }
      : asset
  )
}

export const isAvailableOnDeribit = (instruments: Instrument[]): boolean =>
  instruments.some((instrument) => instrument.exchange === Exchange.EXCHANGE_DERIBIT)

// An asset is only ever "crypto"/"equity"/... because of where it actually
// trades, not something stored on the group — so this reads it off the first
// classified instrument. UNSPECIFIED is "not classified yet", not a real class.
export const representativeAssetClass = (instruments: Instrument[]): AssetClass | undefined =>
  instruments.find(
    (instrument) =>
      instrument.enrichment?.assetClass !== undefined &&
      instrument.enrichment.assetClass !== AssetClass.ASSET_CLASS_UNSPECIFIED
  )?.enrichment?.assetClass

// Classification belongs to the asset group: a venue with incomplete enrichment
// must not disappear when another contract in the same canonical group is classified.
export function filterAssetsByClass(
  assets: WizardAsset[],
  assetClass: AssetClass | null
): WizardAsset[] {
  if (assetClass === null) return assets
  return assets
    .filter((asset) =>
      asset.instruments.some((instrument) => instrument.enrichment?.assetClass === assetClass)
    )
    .map((asset) => ({ ...asset, assetClass }))
}

// Best-effort ranking signal within the same venue-priority bucket. This is
// not a normalized cross-venue figure, so it is only a secondary signal after
// Deribit availability and the major-asset ordering.
export function assetVolume(instruments: Instrument[]): number {
  return instruments.reduce((max, i) => Math.max(max, safeParseFloat(i.enrichment?.volume24h)), 0)
}

export function sortAssets(assets: WizardAsset[]): WizardAsset[] {
  return [...assets].sort((a, b) => {
    const majorA = MAJOR_ASSETS.indexOf(a.key)
    const majorB = MAJOR_ASSETS.indexOf(b.key)
    if (majorA !== -1 || majorB !== -1) {
      if (majorA === -1) return 1
      if (majorB === -1) return -1
      return majorA - majorB
    }

    const deribitDiff = Number(b.hasDeribit) - Number(a.hasDeribit)
    if (deribitDiff !== 0) return deribitDiff

    const volumeDiff = b.volume24h - a.volume24h
    if (volumeDiff !== 0) return volumeDiff
    // All-zero-volume (backend not deployed yet, or a venue that doesn't
    // report volume) falls back here — pure alphabetical, not a special case.
    return a.key.localeCompare(b.key)
  })
}

export interface WizardOptionExpiryGroup {
  expiryMs: number
  label: string
  contracts: Instrument[]
}

export interface WizardLevel3Sections {
  perpetuals: Instrument[]
  spot: Instrument[]
  futures: Instrument[]
  optionsByExpiry: WizardOptionExpiryGroup[]
}

const expiryMsOf = (i: Instrument): number => Number(i.expirationTsMs ?? 0)

export function buildLevel3Sections(instruments: Instrument[]): WizardLevel3Sections {
  const perpetuals = instruments.filter(
    (i) => i.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE && isPerpInstrument(i)
  )
  const spot = instruments.filter((i) => i.kind === InstrumentKind.INSTRUMENT_KIND_SPOT)
  const futures = instruments
    .filter((i) => i.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE && !isPerpInstrument(i))
    .sort((a, b) => expiryMsOf(a) - expiryMsOf(b))
  const options = instruments.filter((i) => i.kind === InstrumentKind.INSTRUMENT_KIND_OPTION)

  return { perpetuals, spot, futures, optionsByExpiry: groupOptionsByExpiry(options) }
}

function groupOptionsByExpiry(options: Instrument[]): WizardOptionExpiryGroup[] {
  const groups = new Map<number, Instrument[]>()
  for (const option of options) {
    const expiryMs = expiryMsOf(option)
    const existing = groups.get(expiryMs)
    if (existing) {
      existing.push(option)
    } else {
      groups.set(expiryMs, [option])
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([expiryMs, contracts]) => ({
      expiryMs,
      label: formatExpiryLabel(expiryMs),
      contracts: [...contracts].sort(
        (a, b) =>
          safeParseFloat(a.strikePrice) - safeParseFloat(b.strikePrice) ||
          (a.optionType ?? 0) - (b.optionType ?? 0) // Call (1) before Put (2)
      ),
    }))
}

/**
 * Formatted in UTC, not the viewer's zone.
 *
 * Option expiries land on a UTC instant (08:00 UTC on Deribit), and the label names
 * the *contract*, not a local moment — `BTC-27JUN25` has to read as 27 Jun to
 * everyone. Rendering it locally shifted the date a day earlier for every user west
 * of Greenwich, so a New York trader saw an expiry that did not exist.
 */
export function formatExpiryLabel(expiryMs: number): string {
  if (!expiryMs) return 'Unknown expiry'
  return format(expiryMs, 'MMM dd, yy', { in: UTC })
}
